import Autocomplete from '@mui/material/Autocomplete';
import InputBase from '@mui/material/InputBase';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';
import { lookupGeoLocations, lookupPostalCodes } from '../../campaign-wizard.api';
import { GeoOption } from '../../campaign-wizard.types';
import { wz } from '../../components/wizard-tokens';
import { GeoErrorKind, geoQueryReady } from './candidates-logic';
import { startGeoSearch } from './geo-search';

interface Props {
  /** Das im Wizard gewählte Absender-Konto; ohne Konto lässt sich kein Ort suchen. */
  accountId: string;
  /** Startwert der Suche, z. B. der vorgeschlagene Ort „Köln“ beim Bestätigen. */
  initial?: string;
  /** `postal` = „PLZ + Umkreis“ (PLZ oder Stadt eingeben, Treffer sind PLZ-Einträge von LinkedIn); Standard: Ortssuche. */
  mode?: 'city' | 'postal';
  /** Kontoart (Sales Navigator / Recruiter) für die PLZ-Suche. */
  productType?: string | null;
  /** Eingabe zum Zeitpunkt der Auswahl (daraus liest die PLZ-Suche die PLZ, wenn der Treffertext keine enthält). */
  onSelect: (option: GeoOption, query: string) => void;
  onCancel: () => void;
}

/**
 * „Ort hinzufügen“ / „Ort bestätigen“: Autovervollständigung über die LinkedIn-Ortssuche. Gesucht wird entprellt ab zwei
 * Zeichen, angeboten werden bis zu acht echte LinkedIn-Orte; freier Text wird nicht als Ort übernommen.
 */
const LocationPicker: React.FC<Props> = ({
  accountId,
  initial = '',
  mode = 'city',
  productType,
  onSelect,
  onCancel,
}) => {
  const { classes } = useStyles();
  const { t, i18n } = useTranslation();
  const boxRef = useRef<HTMLDivElement | null>(null);
  const [input, setInput] = useState(initial);
  const [open, setOpen] = useState(true);
  const [options, setOptions] = useState<GeoOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<GeoErrorKind | null>(null);

  useEffect(() => {
    boxRef.current?.querySelector('input')?.focus();
  }, []);

  const lang = i18n.language?.startsWith('en') ? 'en' : 'de';
  useEffect(
    () =>
      startGeoSearch(
        accountId,
        input,
        (account, query, signal) =>
          mode === 'postal'
            ? lookupPostalCodes(account, query, productType, signal, lang)
            : lookupGeoLocations(account, query, signal, lang),
        { onLoading: setLoading, onOptions: setOptions, onError: setError }
      ),
    [accountId, input, lang, mode, productType]
  );

  const noOptionsText = !accountId
    ? t('wizCandGeoNoAccount')
    : error === 'session'
      ? t('wizCandGeoSession')
      : error === 'failed'
        ? t('wizCandGeoFailed')
        : !geoQueryReady(input)
          ? t('wizCandGeoMinChars')
          : mode === 'postal'
            ? t('wizCandPostalNone')
            : t('wizCandGeoNone');

  return (
    <Autocomplete<GeoOption>
      open={open}
      onOpen={() => setOpen(true)}
      onClose={(_, reason) => {
        if (reason === 'escape') onCancel();
        else setOpen(false);
      }}
      value={null}
      inputValue={input}
      onInputChange={(_, value, reason) => {
        if (reason === 'input' || reason === 'clear') setInput(value);
      }}
      options={options}
      filterOptions={all => all}
      getOptionLabel={o => o.text}
      isOptionEqualToValue={(a, b) => a.id === b.id}
      onChange={(_, option) => {
        if (option) onSelect(option, input);
      }}
      loading={loading}
      loadingText={t('wizCandGeoLoading')}
      noOptionsText={noOptionsText}
      autoHighlight
      openOnFocus
      blurOnSelect
      className={classes.root}
      slotProps={{ paper: { className: classes.paper } }}
      renderOption={(props, option) => (
        <li {...props} key={option.id}>
          <span>{option.text}</span>
          {option.region ? <span className={classes.tag}>{t('wizCandGeoRegionTag')}</span> : null}
        </li>
      )}
      renderInput={params => (
        <div
          ref={node => {
            boxRef.current = node;
            (params.InputProps.ref as React.RefCallback<HTMLDivElement>)(node);
          }}
          className={classes.box}
        >
          <InputBase
            inputProps={params.inputProps}
            id={params.id}
            disabled={params.disabled}
            fullWidth
            className={classes.input}
            placeholder={mode === 'postal' ? t('wizCandPostalPlaceholder') : t('wizCandLocationPlaceholder')}
            onBlur={() => onCancel()}
          />
        </div>
      )}
    />
  );
};

const useStyles = makeStyles()({
  root: { width: 260 },
  box: {
    display: 'flex',
    alignItems: 'center',
    fontSize: 13,
    borderRadius: 14,
    padding: '2px 10px',
    border: `1px solid ${wz.primary}`,
    background: '#FFF',
  },
  input: { fontSize: 13, fontFamily: 'inherit', color: wz.text, '& input': { padding: '2px 0' } },
  paper: { boxShadow: wz.shadowMenu, borderRadius: 10, fontSize: 14 },
  tag: { marginLeft: 8, fontSize: 11, color: wz.muted },
});

export default LocationPicker;
